import { useState, useEffect, useCallback } from 'react';
import { fetchAllPages } from '../../../lib/api';
import { toast } from 'sonner';
import { coreAPI } from '../../../lib/api';
import {
  barrattAPI,
  type ImportSummary,
  type ImportLog,
  type ImportException,
  type ImportTemplate,
  type UploadPeriod,
} from '../../../lib/api/barratt';
import { getErrorMessage } from '../../../hooks/useFetchData';
import { useIsCustomer } from '../../../hooks/usePortalMode';
import { currentPeriodKey, downloadTemplateCsv, periodKeyLabel, periodLabelFor, datasetLabel } from '../uploadCycle';

// Allowed file types for upload (must match backend ALLOWED_IMPORT_MIME_TYPES)
const ALLOWED_FILE_EXTENSIONS = ['.csv', '.xlsx', '.xls', '.pdf', '.doc', '.docx', '.png', '.jpg', '.jpeg', '.gif'];
export const ALLOWED_FILE_ACCEPT = ALLOWED_FILE_EXTENSIONS.join(',');
export const ALLOWED_FILE_TYPES_MESSAGE = 'Allowed file types: CSV, XLSX, XLS, PDF, DOC, DOCX, PNG, JPG, JPEG, GIF';

const isValidFileType = (filename: string): boolean => {
  const ext = '.' + filename.split('.').pop()?.toLowerCase();
  return ALLOWED_FILE_EXTENSIONS.includes(ext);
};

/** 'Supplier_Performance' → 'Supplier Performance' */
export const prettyTemplateName = (name: string): string => name.replace(/_/g, ' ');

const normalise = (name: string): string => name.toLowerCase().replace(/[_\s]+/g, ' ').trim();

export interface SupplierOption {
  id: number;
  name: string;
}

export interface UseDataUploadResult {
  templates: ImportTemplate[];
  templatesLoading: boolean;
  templatesError: string | null;
  selectedTemplate: string;
  setSelectedTemplate: (id: string) => void;
  selectedTemplateData: ImportTemplate | undefined;
  periods: UploadPeriod[];
  period: string;
  setPeriod: (period: string) => void;
  /** Customer users only: suppliers they can upload on behalf of. */
  suppliers: SupplierOption[];
  showSupplierSelect: boolean;
  supplierId: string;
  setSupplierId: (id: string) => void;
  isUploading: boolean;
  uploadProgress: number;
  currentImport: ImportSummary | null;
  importHistory: ImportSummary[];
  isLoadingHistory: boolean;
  historyError: string | null;
  fetchImportHistory: () => Promise<void>;
  handleFileUpload: (file: File) => Promise<void>;
  handleDownloadTemplate: () => void;
  templateNameFor: (imp: ImportSummary) => string;
  deleteImport: (id: number) => Promise<void>;
  details: {
    open: boolean;
    setOpen: (open: boolean) => void;
    importSummary: ImportSummary | null;
    logs: ImportLog[];
    exceptions: ImportException[];
    view: (imp: ImportSummary) => Promise<void>;
  };
}

/**
 * Everything the Data Upload page needs: templates and periods from the API,
 * the supplier list for on-behalf uploads, the upload itself, and the
 * import history with its detail dialog.
 *
 * Calls: GET import-summaries/templates/, GET supplier-uploads/periods/,
 * GET core/companies/for-filter/ (customers), GET import-summaries/,
 * POST import-summaries/upload/ (file, template_id, period, supplier_id),
 * GET import-summaries/{id}/logs/ + /exceptions/, DELETE import-summaries/{id}/.
 *
 * @returns Page state and handlers
 */
export function useDataUpload(): UseDataUploadResult {
  const isCustomer = useIsCustomer();

  const [templates, setTemplates] = useState<ImportTemplate[]>([]);
  const [templatesLoading, setTemplatesLoading] = useState(true);
  const [templatesError, setTemplatesError] = useState<string | null>(null);
  const [selectedTemplate, setSelectedTemplate] = useState('');

  const [periods, setPeriods] = useState<UploadPeriod[]>([]);
  const [period, setPeriod] = useState(currentPeriodKey());

  const [suppliers, setSuppliers] = useState<SupplierOption[]>([]);
  const [supplierId, setSupplierId] = useState('');

  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [currentImport, setCurrentImport] = useState<ImportSummary | null>(null);

  const [importHistory, setImportHistory] = useState<ImportSummary[]>([]);
  const [isLoadingHistory, setIsLoadingHistory] = useState(true);
  const [historyError, setHistoryError] = useState<string | null>(null);

  const [detailsOpen, setDetailsOpen] = useState(false);
  const [selectedImport, setSelectedImport] = useState<ImportSummary | null>(null);
  const [importLogs, setImportLogs] = useState<ImportLog[]>([]);
  const [importExceptions, setImportExceptions] = useState<ImportException[]>([]);

  const fetchImportHistory = useCallback(async () => {
    setIsLoadingHistory(true);
    setHistoryError(null);
    try {
      // Every page — the table searches, sorts and pages client-side.
      const all = await fetchAllPages<ImportSummary>((page) => barrattAPI.getImportSummaries({ page_size: 100, page }));
      setImportHistory(all);
    } catch (err: unknown) {
      console.error('Error fetching import history:', err);
      setHistoryError(getErrorMessage(err, 'Failed to load import history'));
    } finally {
      setIsLoadingHistory(false);
    }
  }, []);

  // Templates are the single source of truth for names + columns.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await barrattAPI.getImportTemplates();
        if (!cancelled) setTemplates(data.templates ?? []);
      } catch (err: unknown) {
        console.error('Error fetching import templates:', err);
        if (!cancelled) setTemplatesError(getErrorMessage(err, 'Failed to load templates'));
      } finally {
        if (!cancelled) setTemplatesLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Periods default to the current one; a failed load leaves the client-side current period.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await barrattAPI.getUploadPeriods();
        if (cancelled) return;
        const list = data.periods ?? [];
        setPeriods(list);
        const current = list.find((p) => p.is_current) ?? list[0];
        if (current) setPeriod(current.period);
      } catch (err: unknown) {
        console.error('Error fetching upload periods:', err);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Portal-aware supplier list (same source as Spend Data).
  useEffect(() => {
    if (!isCustomer) return;
    let cancelled = false;
    (async () => {
      try {
        const resp = await coreAPI.getSupplierSitesForFilter();
        if (cancelled) return;
        const raw = resp.data as unknown;
        const results: SupplierOption[] =
          Array.isArray(raw) ? (raw as SupplierOption[])
          : Array.isArray((raw as { results?: unknown }).results)
            ? (raw as { results: SupplierOption[] }).results
            : [];
        setSuppliers(
          results
            .map((c) => ({ id: c.id, name: c.name || `Supplier ${c.id}` }))
            .sort((a, b) => a.name.localeCompare(b.name)),
        );
      } catch { /* dropdown stays empty */ }
    })();
    return () => { cancelled = true; };
  }, [isCustomer]);

  useEffect(() => {
    void fetchImportHistory();
  }, [fetchImportHistory]);

  const selectedTemplateData = templates.find((t) => t.id.toString() === selectedTemplate);

  const handleFileUpload = useCallback(async (file: File) => {
    if (!selectedTemplate) {
      toast.error('Please select a template before uploading');
      return;
    }
    if (!isValidFileType(file.name)) {
      toast.error(`Invalid file type. ${ALLOWED_FILE_TYPES_MESSAGE}`);
      return;
    }

    const formData = new FormData();
    formData.append('file', file);
    formData.append('template_id', selectedTemplate);
    if (period) formData.append('period', period);
    if (supplierId) formData.append('supplier_id', supplierId);

    setIsUploading(true);
    setUploadProgress(0);
    // Simulate progress during upload
    const progressInterval = setInterval(() => {
      setUploadProgress((prev) => Math.min(prev + 10, 90));
    }, 300);

    try {
      const response = await barrattAPI.uploadFile(formData);
      setUploadProgress(100);
      setCurrentImport(response.data);

      // The endpoint answers 201 whether or not the file validated, so a
      // rejected file used to raise "File uploaded successfully" here too. The
      // reasons now ride on the response; the first one goes in the message and
      // the rest are one click away in the import's details.
      if (response.data.is_success === false) {
        const first = (response.data.exceptions ?? []).find((e) => e.severity === 'error');
        const errors = response.data.exception_counts?.error ?? 0;
        toast.error(
          first
            ? `${file.name} was not accepted: ${first.message}${errors > 1 ? ` (and ${errors - 1} more)` : ''}`
            : `${file.name} was not accepted. Open the import below to see why.`,
        );
        await fetchImportHistory();
        return;
      }

      toast.success('File uploaded successfully');

      const tracking = response.data.tracking;
      if (tracking) {
        const label = tracking.period_label ?? (tracking.period_start ? periodLabelFor(tracking) : periodKeyLabel(period));
        toast.success(`Marked ${label} ${datasetLabel(tracking)} as uploaded for ${tracking.supplier_name ?? 'supplier'}`);
      }

      await fetchImportHistory();
    } catch (err: unknown) {
      console.error('Error uploading file:', err);
      toast.error(getErrorMessage(err, 'Failed to upload file'));
    } finally {
      clearInterval(progressInterval);
      setIsUploading(false);
      setTimeout(() => setUploadProgress(0), 1000);
    }
  }, [selectedTemplate, period, supplierId, fetchImportHistory]);

  // Header-only CSV built from the API's column list.
  const handleDownloadTemplate = useCallback(() => {
    if (!selectedTemplateData) return;
    downloadTemplateCsv(selectedTemplateData);
    toast.success('Template downloaded');
  }, [selectedTemplateData]);

  // Template id first, then the dataset name matched against template names, then whatever the API labelled it.
  const templateNameFor = useCallback((imp: ImportSummary): string => {
    const byId = imp.template ? templates.find((t) => t.id === imp.template) : undefined;
    if (byId) return prettyTemplateName(byId.name);
    if (imp.dataset_name) {
      const byName = templates.find((t) => normalise(t.name) === normalise(imp.dataset_name ?? ''));
      if (byName) return prettyTemplateName(byName.name);
    }
    return imp.template_display || imp.dataset_name || 'Unknown';
  }, [templates]);

  const deleteImport = useCallback(async (id: number) => {
    try {
      await barrattAPI.deleteImportSummary(id);
      toast.success('Import record deleted');
      await fetchImportHistory();
    } catch (err: unknown) {
      console.error('Error deleting import:', err);
      toast.error(getErrorMessage(err, 'Failed to delete import record'));
    }
  }, [fetchImportHistory]);

  const viewDetails = useCallback(async (imp: ImportSummary) => {
    setSelectedImport(imp);
    setImportLogs([]);
    setImportExceptions([]);
    setDetailsOpen(true);
    try {
      const [logsRes, exceptionsRes] = await Promise.all([
        barrattAPI.getImportSummaryLogs(imp.id),
        barrattAPI.getImportSummaryExceptions(imp.id),
      ]);
      setImportLogs(logsRes.data);
      setImportExceptions(exceptionsRes.data);
    } catch (err: unknown) {
      console.error('Error fetching import details:', err);
      toast.error(getErrorMessage(err, 'Failed to load import details'));
    }
  }, []);

  return {
    templates,
    templatesLoading,
    templatesError,
    selectedTemplate,
    setSelectedTemplate,
    selectedTemplateData,
    periods,
    period,
    setPeriod,
    suppliers,
    showSupplierSelect: isCustomer,
    supplierId,
    setSupplierId,
    isUploading,
    uploadProgress,
    currentImport,
    importHistory,
    isLoadingHistory,
    historyError,
    fetchImportHistory,
    handleFileUpload,
    handleDownloadTemplate,
    templateNameFor,
    deleteImport,
    details: {
      open: detailsOpen,
      setOpen: setDetailsOpen,
      importSummary: selectedImport,
      logs: importLogs,
      exceptions: importExceptions,
      view: viewDetails,
    },
  };
}
